import { useState, useEffect, useRef } from 'react';
import { setChatContainerStyles } from '../Store/features/chat/chatSlice';
import { useAppDispatch } from '../store';

interface UseResizableContainerProps {
    chatId: string | undefined;
    chatWidth: number;
    initialWidth?: number;
    minWidth?: number;
}

const useResizableContainer = ({ chatId, chatWidth, minWidth = 450 }: UseResizableContainerProps) => {
    const dispatch = useAppDispatch();
    const resizeContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(
        parseInt(localStorage.getItem('chatContainerWidth') || '0'),
    );

    useEffect(() => {
        if (containerWidth < 450) return;
        dispatch(setChatContainerStyles({ maxWidth: `${containerWidth}px` }));
    }, [containerWidth, dispatch]);

    useEffect(() => {
        if (chatId === undefined) return;

        const handleResize = () => {
            setTimeout(() => {
                const container = document.querySelector('.clean-container');
                if (!container) return;

                const defaultWidth = container.clientWidth || 0;
                setContainerWidth(defaultWidth);
                dispatch(setChatContainerStyles({ maxWidth: `${defaultWidth}px` }));
                localStorage.setItem('chatContainerWidth', defaultWidth.toString());
            }, 100);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [chatId, chatWidth, minWidth, containerWidth, dispatch]);

    useEffect(() => {
        if (containerWidth > chatWidth) {
            const validatedWidth = Math.min(containerWidth, chatWidth);
            setContainerWidth(validatedWidth);
            dispatch(setChatContainerStyles({ maxWidth: `${validatedWidth}px` }));
            localStorage.setItem('chatContainerWidth', validatedWidth.toString());
        }
    }, [chatWidth, containerWidth, dispatch]);

    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (chatId === undefined) return;

        event.preventDefault();
        const startX = event.clientX;
        const initialWidth = resizeContainerRef.current?.clientWidth || containerWidth;

        const mouseMoveHandler = (event: MouseEvent) => {
            const deltaX = event.clientX - startX;
            const newWidth = initialWidth + deltaX;

            const validatedWidth = Math.min(Math.max(newWidth, minWidth), chatWidth);
            setContainerWidth(validatedWidth);
            dispatch(setChatContainerStyles({ maxWidth: `${validatedWidth}px` }));

            localStorage.setItem('chatContainerWidth', validatedWidth.toString());
        };

        const mouseUpHandler = () => {
            document.removeEventListener('mousemove', mouseMoveHandler);
            document.removeEventListener('mouseup', mouseUpHandler);
        };

        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
    };

    return { resizeContainerRef, containerWidth, handleMouseDown };
};

export default useResizableContainer;
